import {
	autoUpdate,
	FloatingPortal,
	flip,
	offset,
	shift,
	useClick,
	useDismiss,
	useFloating,
	useInteractions,
} from "@floating-ui/react";
import type { Column, SortDirection } from "@tanstack/react-table";
import { type RefObject, useCallback, useMemo, useRef, useState } from "react";
import { cn } from "../../lib/utils";
import { Button, ClickableIcon, Icon } from "../../primitives";
import { Checkbox } from "../Checkbox";
import { useExternalFacetValues } from "../DynamicDataTable/ExternalFacetValuesContext";
import { SearchInput } from "../SearchInput";
import { Separator } from "../Separator";

const ButtonIcon = ({
	isFiltered,
	sortDirection,
	headerLabel,
	onTrigger,
}: {
	isFiltered: boolean;
	sortDirection: false | SortDirection;
	headerLabel: string | undefined;
	onTrigger: (e: React.MouseEvent) => void;
}) => {
	const ariaLabel = headerLabel ? `Filter ${headerLabel}` : "Filter column";

	if (isFiltered) {
		if (Boolean(sortDirection)) {
			return (
				<ClickableIcon
					className="w-4 h-5 rounded-sm shrink-0"
					onClick={onTrigger}
					aria-label={ariaLabel}
					type={sortDirection === "asc" ? "funnel-up" : "funnel-down"}
					size="mini"
					color="brand"
				/>
			);
		}
		return (
			<ClickableIcon
				className="w-4 h-5 rounded-sm shrink-0"
				onClick={onTrigger}
				aria-label={ariaLabel}
				type="funnel"
				size="mini"
				color="brand"
			/>
		);
	}

	if (Boolean(sortDirection)) {
		return (
			<ClickableIcon
				onClick={onTrigger}
				className="w-4 h-5 rounded-sm shrink-0"
				aria-label={ariaLabel}
				type={sortDirection === "asc" ? "chevron-up" : "chevron-down"}
				size="mini"
				color="brand"
			/>
		);
	}

	return (
		<ClickableIcon
			onClick={onTrigger}
			className="w-4 h-5 rounded-sm shrink-0"
			aria-label={ariaLabel}
			type="chevron-up-down"
			size="mini"
			color="brand"
		/>
	);
};

export const ColumnHeaderFilterDropdown = <TData,>({
	column,
	triggerRef,
	clickXRef,
	anchorRef,
}: {
	column: Column<TData>;
	triggerRef?: RefObject<HTMLButtonElement | null>;
	clickXRef?: RefObject<number | undefined>;
	anchorRef?: RefObject<HTMLDivElement | null>;
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [search, setSearch] = useState("");
	const searchRef = useRef<HTMLInputElement>(null);

	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		onOpenChange: (open, _event, reason) => {
			if (!open && reason === "outside-press") {
				const blocker = (e: MouseEvent) => {
					e.stopPropagation();
					e.preventDefault();
					document.removeEventListener("click", blocker, true);
				};
				document.addEventListener("click", blocker, true);
			}
			if (open && clickXRef?.current !== undefined && anchorRef?.current) {
				const cellRect = anchorRef.current.getBoundingClientRect();
				const clickX = clickXRef.current;
				clickXRef.current = undefined;
				refs.setReference({
					getBoundingClientRect: () => ({
						x: clickX,
						y: cellRect.y,
						top: cellRect.top,
						bottom: cellRect.bottom,
						left: clickX,
						right: clickX,
						width: 0,
						height: cellRect.height,
					}),
				});
			}
			setIsOpen(open);
			if (!open) setSearch("");
		},
		placement: "bottom-start",
		middleware: [offset(4), flip(), shift({ padding: 12 })],
		whileElementsMounted: autoUpdate,
	});

	const click = useClick(context);
	const dismiss = useDismiss(context);
	const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

	const canSort = column.getCanSort();
	const hasCheckboxFilter = column.columnDef.meta?.headerFilterType === "checkbox";
	const filterValue = (column.getFilterValue() as string[] | undefined) ?? [];
	const filterValueSet = useMemo(() => new Set(filterValue), [filterValue]);
	const isFiltered = filterValue.length > 0;
	const sortDirection = column.getIsSorted();

	const externalFacets = useExternalFacetValues(column.id);
	const facetedValues = externalFacets ?? column.getFacetedUniqueValues();
	const uniqueValues = useMemo(() => {
		const values = new Set<string>();
		for (const [val] of facetedValues) {
			if (Array.isArray(val)) {
				for (const item of val) {
					if (item != null && String(item) !== "") values.add(String(item));
				}
			} else if (val != null && String(val) !== "" && String(val) !== "--") {
				values.add(String(val));
			}
		}
		return Array.from(values).sort((a, b) => a.localeCompare(b));
	}, [facetedValues]);

	const filteredValues = useMemo(() => {
		const base = search
			? uniqueValues.filter((v) => v.toLowerCase().includes(search.toLowerCase()))
			: uniqueValues;
		if (filterValue.length === 0) return base;
		const selected = new Set(filterValue);
		return [...base.filter((v) => selected.has(v)), ...base.filter((v) => !selected.has(v))];
	}, [uniqueValues, search, filterValue]);

	const toggleValue = (value: string) => {
		const current = [...filterValue];
		const idx = current.indexOf(value);
		if (idx >= 0) {
			current.splice(idx, 1);
		} else {
			current.push(value);
		}
		column.setFilterValue(current.length > 0 ? current : undefined);
		searchRef.current?.focus();
	};

	const selectAll = () => {
		const merged = new Set([...filterValue, ...filteredValues]);
		column.setFilterValue([...merged]);
		searchRef.current?.focus();
	};

	const resetFilters = () => {
		column.setFilterValue(undefined);
		setSearch("");
		searchRef.current?.focus();
	};

	const headerLabel =
		typeof column.columnDef.header === "string" ? column.columnDef.header : undefined;

	const handleTrigger = (e: React.MouseEvent) => {
		e.stopPropagation();
		triggerRef?.current?.click();
	};

	const setTriggerButtonRef = useCallback(
		(el: HTMLButtonElement | null) => {
			refs.setReference(el);
			if (triggerRef) triggerRef.current = el;
		},
		[refs, triggerRef],
	);

	return (
		<div className="flex items-center">
			<ButtonIcon
				isFiltered={isFiltered}
				sortDirection={sortDirection}
				headerLabel={headerLabel}
				onTrigger={handleTrigger}
			/>
			<button
				ref={setTriggerButtonRef}
				{...getReferenceProps({
					onClick: (e) => e.stopPropagation(),
				})}
				className="invisible"
			></button>
			{isOpen && (
				<FloatingPortal>
					<div
						ref={refs.setFloating}
						style={floatingStyles}
						{...getFloatingProps({
							onClick: (e) => e.stopPropagation(),
						})}
						className="z-[100] bg-surface-base border border-bc-default rounded-lg shadow-md min-w-[200px] max-w-[300px]"
					>
						{canSort && (
							<div className="py-1">
								<Button
									variant="ghost"
									className={cn(
										"w-full flex justify-start h-auto items-center gap-2 px-3 py-2 text-sm text-secondary hover:bg-surface-light cursor-pointer",
										sortDirection === "asc" && "text-primary font-medium",
									)}
									onClick={() =>
										sortDirection === "asc" ? column.clearSorting() : column.toggleSorting(false)
									}
									leadingIcon={
										<Icon
											type="arrow-up"
											size="micro"
											color={sortDirection === "asc" ? "primary" : "tertiary"}
										/>
									}
								>
									Sort ascending
								</Button>
								<Button
									variant="ghost"
									className={cn(
										"w-full flex justify-start h-auto items-center gap-2 px-3 py-2 text-sm text-secondary hover:bg-surface-light cursor-pointer",
										sortDirection === "desc" && "text-primary font-medium",
									)}
									onClick={() =>
										sortDirection === "desc" ? column.clearSorting() : column.toggleSorting(true)
									}
									leadingIcon={
										<Icon
											type="arrow-down"
											size="micro"
											color={sortDirection === "desc" ? "primary" : "tertiary"}
										/>
									}
								>
									Sort descending
								</Button>
							</div>
						)}

						{hasCheckboxFilter && (
							<>
								{canSort && <Separator className="bg-bc-default" />}

								<div className="px-3 py-2">
									<SearchInput
										ref={searchRef}
										autofocus
										placeholder="Search..."
										onChange={setSearch}
										className="w-full !rounded-md !h-[32px] border border-bc-default"
										hideFocusStyles
									/>
								</div>

								<div className="max-h-[240px] overflow-y-auto scrollbar-med">
									{filteredValues.map((value) => (
										<label
											key={value}
											className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-light cursor-pointer"
										>
											<Checkbox
												checked={filterValueSet.has(value)}
												onChange={() => toggleValue(value)}
												condensed
											/>
											<span className="text-sm text-secondary truncate">{value}</span>
										</label>
									))}
									{filteredValues.length === 0 && (
										<div className="px-3 py-2 text-sm text-tertiary italic">No matches</div>
									)}
								</div>

								<Separator className="bg-bc-default" />

								<div className="flex items-center justify-between px-3 py-2">
									<span className="text-xs text-tertiary">Selected: {filterValue.length}</span>
									{isFiltered ? (
										<button
											className="text-xs text-brand-accent hover:underline cursor-pointer"
											onClick={resetFilters}
										>
											Reset filters
										</button>
									) : (
										<button
											className="text-xs text-brand-accent hover:underline cursor-pointer"
											onClick={selectAll}
										>
											{search ? "Select matching" : "Select all"}
										</button>
									)}
								</div>
							</>
						)}
					</div>
				</FloatingPortal>
			)}
		</div>
	);
};
